import { Component, type ErrorInfo, type ReactNode } from "react";
import { AlertTriangle, RefreshCw, Home } from "lucide-react";

/**
 * ErrorBoundary — catches render-phase errors in any descendant React tree
 * and shows a graceful fallback instead of unmounting the whole app.
 *
 * Why this matters:
 *   - Pre-2026-05-02, a Hooks-after-early-return bug in Overview.tsx caused the
 *     entire cockpit to white-screen on every page load. Without a boundary,
 *     even a small render error in one card brings down /settings, /finance,
 *     /audit, etc.
 *   - With this boundary in place, an error in (say) the Acute strip leaves
 *     navigation, sidebar, and other sections untouched.
 *
 * Reset behavior:
 *   - The key prop on `<ErrorBoundary key={location.pathname}>` makes React
 *     remount the boundary (and clear error state) when the route changes.
 *   - The "Try again" button clears state without remount.
 *
 * Production-safe:
 *   - In production builds, error.message is shown but stack is hidden behind
 *     a collapsible disclosure to avoid leaking minified internals.
 *   - All caught errors are reported to the browser console via `console.error`
 *     (matches existing observability — wrangler tail captures them server-side
 *     via SSE error path if applicable).
 */
interface Props {
  children: ReactNode;
  /** Optional custom fallback render fn — receives error + reset() */
  fallback?: (error: Error, reset: () => void) => ReactNode;
  /** Optional handler called on every caught error — useful for telemetry */
  onError?: (error: Error, info: ErrorInfo) => void;
}

interface State {
  error: Error | null;
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    // Always log to console — captured by browser devtools + any external
    // observability hooks that intercept console.error.
    console.error("[ErrorBoundary] caught render error:", error, info);
    this.props.onError?.(error, info);
  }

  reset = (): void => {
    this.setState({ error: null });
  };

  render(): ReactNode {
    const { error } = this.state;
    if (!error) return this.props.children;

    if (this.props.fallback) {
      return this.props.fallback(error, this.reset);
    }

    return <DefaultFallback error={error} reset={this.reset} />;
  }
}

function DefaultFallback({ error, reset }: { error: Error; reset: () => void }) {
  const isDev = import.meta.env.DEV;
  return (
    <div className="rounded-lg border border-red-900/40 bg-red-950/20 p-6 my-6">
      <div className="flex items-start gap-3">
        <AlertTriangle className="h-5 w-5 text-red-400 flex-shrink-0 mt-0.5" />
        <div className="flex-1 min-w-0">
          <h2 className="text-base font-semibold text-red-200">Something went wrong here</h2>
          <p className="text-sm text-red-300/80 mt-1">
            This section crashed but the rest of the cockpit is still working. Try the actions
            below.
          </p>
          <div className="text-xs font-mono text-red-300/90 bg-red-950/40 border border-red-900/40 rounded px-2.5 py-1.5 mt-3 break-words">
            {error.message || "Unknown error"}
          </div>

          {isDev && error.stack && (
            <details className="mt-3">
              <summary className="text-[11px] text-red-400/80 cursor-pointer hover:text-red-300">
                Stack trace (dev only)
              </summary>
              <pre className="text-[10px] font-mono text-red-300/70 bg-red-950/40 border border-red-900/40 rounded px-2.5 py-1.5 mt-1.5 overflow-x-auto max-h-48">
                {error.stack}
              </pre>
            </details>
          )}

          <div className="flex items-center gap-2 mt-4">
            <button
              type="button"
              onClick={reset}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-red-900/40 bg-red-950/30 text-sm text-red-200 hover:bg-red-950/50"
            >
              <RefreshCw className="h-3.5 w-3.5" />
              Try again
            </button>
            <a
              href="/"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-slate-700 bg-slate-900 text-sm text-slate-300 hover:bg-slate-800"
            >
              <Home className="h-3.5 w-3.5" />
              Back to overview
            </a>
            <button
              type="button"
              onClick={() => window.location.reload()}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm text-slate-400 hover:text-slate-200"
            >
              Hard reload
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * Convenience HOC for wrapping a component with an ErrorBoundary.
 *
 * Usage:
 *   const SafeOverview = withErrorBoundary(Overview);
 */
export function withErrorBoundary<P extends object>(
  Wrapped: React.ComponentType<P>,
  errorBoundaryProps?: Omit<Props, "children">
): React.ComponentType<P> {
  return function WithErrorBoundary(props: P) {
    return (
      <ErrorBoundary {...errorBoundaryProps}>
        <Wrapped {...props} />
      </ErrorBoundary>
    );
  };
}
